import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Clock, Play, FileText, Pill, AlertTriangle, CheckCircle,
  RefreshCw, Wifi, WifiOff, ChevronRight, Bell, Calendar,
  ClipboardList, Upload, User, Home, ArrowRight, Camera
} from 'lucide-react';
import { getQueueStats, getAllQueuedActions, getActionDescription, ACTION_STATUS } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline, getSyncingStatus } from '@/lib/offline/syncEngine';
import { initSyncService } from '@/lib/offline/syncService';
import { useMobileDashboard, useNotificationCounts, useCacheInvalidation } from '@/hooks/useQueryCache';
import { MobileBottomNav, MobilePageContainer } from '@/components/mobile/MobileNavigation';
import { useBranding } from '@/lib/branding';

// Minimum touch target size
const MIN_TOUCH_SIZE = 48;

// Initialize sync service on app load
initSyncService();

function ConnectionStatus({ online }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
      online ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
    }`}>
      {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
      {online ? 'Online' : 'Offline'}
    </div>
  );
}

function DashboardCard({ title, icon: Icon, children, onClick, badge, variant = 'default' }) {
  const variants = {
    default: 'bg-card hover:bg-muted/50',
    warning: 'bg-amber-500/5 border-amber-500/20',
    danger: 'bg-red-500/5 border-red-500/20',
    success: 'bg-green-500/5 border-green-500/20',
    primary: 'bg-primary/5 border-primary/20',
  };

  return (
    <Card 
      className={`${variants[variant]} transition-all cursor-pointer active:scale-[0.98]`}
      onClick={onClick}
      data-testid={`mobile-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' :
              'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' :
                'text-primary'
              }`} />
            </div>
            <div>
              <p className="font-medium text-sm">{title}</p>
              {children}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {badge !== undefined && badge !== null && (
              <Badge variant={variant === 'danger' ? 'destructive' : 'secondary'} className="text-xs">
                {badge}
              </Badge>
            )}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickActionButton({ icon: Icon, label, onClick, variant = 'default', disabled }) {
  const variants = {
    default: 'bg-card border-2 hover:border-primary/50',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    success: 'bg-green-500 text-white hover:bg-green-600',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all active:scale-95 disabled:opacity-50 ${variants[variant]}`}
      style={{ minWidth: MIN_TOUCH_SIZE, minHeight: MIN_TOUCH_SIZE }}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-6 w-6 mb-1" />
      <span className="text-xs font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

function SyncQueueWidget({ stats, onSync, syncing, onViewDetails }) {
  const hasPending = stats.pending > 0 || stats.failed > 0;
  
  return (
    <Card 
      className={`cursor-pointer transition-colors ${hasPending ? 'bg-amber-500/5 border-amber-500/20' : 'bg-card'} hover:bg-muted/50`}
      onClick={onViewDetails}
      data-testid="sync-queue-widget"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              hasPending ? 'bg-amber-500/10' : 'bg-green-500/10'
            }`}>
              {syncing ? (
                <RefreshCw className="h-5 w-5 text-primary animate-spin" />
              ) : hasPending ? (
                <Upload className="h-5 w-5 text-amber-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
            </div>
            <div>
              <p className="font-medium text-sm">Sync Queue</p>
              <p className="text-xs text-muted-foreground">
                {syncing ? 'Syncing...' : hasPending ? `${stats.pending + stats.failed} pending` : 'All synced'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant={hasPending ? 'default' : 'outline'}
            onClick={(e) => { e.stopPropagation(); onSync(); }}
            disabled={syncing || !hasPending}
            data-testid="sync-now-btn"
          >
            {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Sync Now'}
          </Button>
        </div>
        {(stats.failed > 0 || stats.conflict > 0) && (
          <div className="mt-3 pt-3 border-t flex gap-2">
            {stats.failed > 0 && (
              <Badge variant="destructive" className="text-xs" data-testid="failed-badge">{stats.failed} failed</Badge>
            )}
            {stats.conflict > 0 && (
              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600">{stats.conflict} conflicts</Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MobileStaffDashboardPage() {
  const navigate = useNavigate();
  const { organisationName } = useBranding();
  const [online, setOnline] = useState(isOnline());
  const [syncing, setSyncing] = useState(false);
  const [queueStats, setQueueStats] = useState({ pending: 0, failed: 0, conflict: 0, synced: 0, total: 0 });
  const { invalidateDashboard, invalidateNotifications } = useCacheInvalidation();
  
  // Use cached dashboard data
  const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useMobileDashboard();
  const { data: notificationCounts, refetch: refetchNotifications } = useNotificationCounts();
  
  const loading = dashboardLoading;
  const notificationCount = notificationCounts?.unread || 0;
  
  // Transform dashboard data
  const data = {
    nextShift: dashboardData?.nextShift || null,
    todayShifts: dashboardData?.todayShifts ? Array(dashboardData.todayShifts).fill({}) : [],
    dueMedications: dashboardData?.dueMedications || 0,
    incompleteNotes: dashboardData?.incompleteNotes || 0,
    pendingHandovers: dashboardData?.pendingHandovers || 0,
    criticalAlerts: dashboardData?.criticalAlerts || 0,
    currentShift: dashboardData?.currentShift || null,
  };

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Queue stats listener
  useEffect(() => {
    const updateStats = async () => {
      const stats = await getQueueStats();
      setQueueStats(stats);
    };
    updateStats();
    
    const handler = () => updateStats();
    window.addEventListener('offlineQueueUpdated', handler);
    window.addEventListener('syncStatusChanged', handler);
    return () => {
      window.removeEventListener('offlineQueueUpdated', handler);
      window.removeEventListener('syncStatusChanged', handler);
    };
  }, []);

  // Refresh data - invalidate cache and refetch
  const fetchData = useCallback(async () => {
    if (!online) return;
    
    invalidateDashboard();
    invalidateNotifications();
    await Promise.all([refetchDashboard(), refetchNotifications()]);
  }, [online, invalidateDashboard, invalidateNotifications, refetchDashboard, refetchNotifications]);

  // Handle sync
  const handleSync = async () => {
    if (!online) {
      toast.error('Cannot sync while offline');
      return;
    }
    
    setSyncing(true);
    try {
      const results = await syncAllActions();
      if (results.error) {
        toast.error(`Sync failed: ${results.error}`);
      } else if (results.synced > 0) {
        toast.success(`Synced ${results.synced} action(s)`);
      } else if (results.total === 0) {
        toast.info('Nothing to sync');
      }
    } finally {
      setSyncing(false);
      const stats = await getQueueStats();
      setQueueStats(stats);
    }
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-staff-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold" data-testid="mobile-dashboard-org-name">{organisationName}</h1>
            <p className="text-xs text-muted-foreground">Support Worker</p>
          </div>
          <div className="flex items-center gap-2">
            <ConnectionStatus online={online} />
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => navigate('/mobile/notifications')}
              data-testid="notification-bell"
            >
              <Bell className="h-4 w-4" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Current/Next Shift Card */}
        {data.currentShift ? (
          <Card className="bg-green-500/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Play className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium">CURRENT SHIFT</p>
                  <p className="font-bold">{data.currentShift.house_name || data.currentShift.location || 'Active Shift'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" variant="outline" onClick={() => navigate('/mobile/shift')}>
                  View Details
                </Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={() => navigate('/mobile/shift/checkout')}>
                  End Shift
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : data.nextShift ? (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-primary font-medium">NEXT SHIFT</p>
                  <p className="font-bold">{data.nextShift.house_name || data.nextShift.location || 'Shift'}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatTime(data.nextShift.start_time || data.nextShift.scheduled_start)}
                  </p>
                </div>
              </div>
              <Button className="w-full" onClick={() => navigate(`/mobile/shift/checkin/${data.nextShift.id}`)}>
                <Play className="h-4 w-4 mr-2" />
                Start Shift
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-4 text-center text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No upcoming shifts</p>
            </CardContent>
          </Card>
        )}

        {/* Sync Queue */}
        <SyncQueueWidget 
          stats={queueStats} 
          onSync={handleSync} 
          syncing={syncing} 
          onViewDetails={() => navigate('/mobile/sync')}
        />

        {/* Dashboard Cards */}
        <div className="space-y-3">
          <DashboardCard
            title="Today's Shifts"
            icon={Calendar}
            badge={data.todayShifts.length || null}
            onClick={() => navigate('/mobile/shifts')}
          >
            <p className="text-xs text-muted-foreground">
              {data.todayShifts.length > 0 ? `${data.todayShifts.length} shift(s) today` : 'No shifts today'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Due Medications"
            icon={Pill}
            badge={data.dueMedications || null}
            variant={data.dueMedications > 0 ? 'warning' : 'default'}
            onClick={() => navigate('/mobile/medications')}
          >
            <p className="text-xs text-muted-foreground">
              {data.dueMedications > 0 ? 'Action required' : 'All administered'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Incomplete Notes"
            icon={FileText}
            badge={data.incompleteNotes || null}
            variant={data.incompleteNotes > 0 ? 'warning' : 'default'}
            onClick={() => navigate('/mobile/notes')}
          >
            <p className="text-xs text-muted-foreground">
              {data.incompleteNotes > 0 ? 'Draft notes pending' : 'All complete'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Pending Handovers"
            icon={ClipboardList}
            badge={data.pendingHandovers || null}
            onClick={() => navigate('/mobile/handovers')}
          >
            <p className="text-xs text-muted-foreground">
              {data.pendingHandovers > 0 ? 'Review required' : 'No pending handovers'}
            </p>
          </DashboardCard>

          <DashboardCard
            title="Critical Alerts"
            icon={Bell}
            badge={data.criticalAlerts || null}
            variant={data.criticalAlerts > 0 ? 'danger' : 'default'}
            onClick={() => navigate('/mobile/alerts')}
          >
            <p className="text-xs text-muted-foreground">
              {data.criticalAlerts > 0 ? 'Immediate attention' : 'No critical alerts'}
            </p>
          </DashboardCard>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="grid grid-cols-5 gap-2">
          <QuickActionButton
            icon={Play}
            label="Start Shift"
            variant={data.currentShift ? 'default' : 'primary'}
            disabled={!!data.currentShift}
            onClick={() => navigate(data.nextShift ? `/mobile/shift/checkin/${data.nextShift.id}` : '/mobile/shifts')}
          />
          <QuickActionButton
            icon={FileText}
            label="Add Note"
            onClick={() => navigate('/mobile/notes/new')}
          />
          <QuickActionButton
            icon={Pill}
            label="Record Med"
            onClick={() => navigate('/mobile/medications/log')}
          />
          <QuickActionButton
            icon={AlertTriangle}
            label="Incident"
            variant="danger"
            onClick={() => navigate('/mobile/incidents/new')}
          />
          <QuickActionButton
            icon={Camera}
            label="Upload"
            onClick={() => navigate('/mobile/attachments')}
          />
        </div>
      </div>
      
      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
